import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Textarea } from "@nextui-org/react";
import {
  FiPlus,
  FiMoreHorizontal,
  FiChevronDown,
  FiChevronUp,
  FiMessageSquare,
} from "react-icons/fi";
import { MdOutlineStarBorder, MdEdit, MdDelete } from "react-icons/md";

import InlineAudioPlayer from "./InlineAudioPlayer";
import ConfirmModal from "./ConfirmModal";
import CallSearchModal from "./CallSearchModal";
import Modal from "../../components/Modal";
import CustomCopyToClipboard from "../../components/Kit/CustomCopyToClipboard";
import { getGradeBackgroundColor } from "../../utils/colorUtils";
import {
  fetchDebriefCalls,
  addCallsToDebrief,
  removeCallFromDebrief,
} from "../../redux/actions/debriefActions";

const DebriefCallsTab = forwardRef(
  ({ debriefId, comments = [], onAddComment, onUpdateComment, onDeleteComment }, ref) => {
    const { t } = useTranslation();

    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [expandedCallId, setExpandedCallId] = useState(null);
    const audioRefsMap = useRef({});
    const [audioTime, setAudioTime] = useState(0);

    useImperativeHandle(ref, () => ({
      seekToComment(callId, timestamp) {
        const player = audioRefsMap.current[callId];
        if (player) {
          player.seekTo(timestamp);
        }
        setExpandedCallId(callId);
        setAudioTime(0);
      },
    }));

    // Inline comment form state
    const [newCommentText, setNewCommentText] = useState("");
    const [addingComment, setAddingComment] = useState(false);

    // Edit comment state
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentContent, setEditingCommentContent] = useState("");

    // Confirm modal
    const [confirmAction, setConfirmAction] = useState(null);

    const loadCalls = async () => {
      setLoading(true);
      const data = await fetchDebriefCalls(debriefId);
      setCalls(data);
      setLoading(false);
    };

    useEffect(() => {
      loadCalls();
    }, [debriefId]);

    const handleAddCalls = async (callIds) => {
      try {
        await addCallsToDebrief(debriefId, callIds);
        setIsSearchModalOpen(false);
        await loadCalls();
      } catch (error) {
        console.error("Error adding calls:", error);
      }
    };

    const handleRemoveCall = (callId) => {
      setMenuOpenId(null);
      setConfirmAction({
        message: t("Retirer cet appel du debrief ?"),
        destructive: true,
        onConfirm: async () => {
          setConfirmAction(null);
          try {
            await removeCallFromDebrief(debriefId, callId);
            await loadCalls();
          } catch (error) {
            console.error("Error removing call:", error);
          }
        },
      });
    };

    const handleAddCallComment = async (callId) => {
      if (!newCommentText.trim() || !onAddComment) return;
      setAddingComment(true);
      const currentTime = audioTime > 0 ? audioTime : null;
      try {
        await onAddComment(newCommentText, callId, currentTime);
        setNewCommentText("");
      } catch (error) {
        console.error("Error adding comment:", error);
      }
      setAddingComment(false);
    };

    const seekAudio = (callId, seconds) => {
      const player = audioRefsMap.current[callId];
      if (player) player.seekTo(seconds);
    };

    const handleUpdateComment = async (commentId) => {
      if (!onUpdateComment) return;
      try {
        await onUpdateComment(commentId, editingCommentContent);
        setEditingCommentId(null);
        setEditingCommentContent("");
      } catch (error) {
        console.error("Error updating comment:", error);
      }
    };

    const handleDeleteComment = (commentId) => {
      if (!onDeleteComment) return;
      setConfirmAction({
        message: t("Supprimer ce commentaire ?"),
        destructive: true,
        onConfirm: async () => {
          setConfirmAction(null);
          try {
            await onDeleteComment(commentId);
          } catch (error) {
            console.error("Error deleting comment:", error);
          }
        },
      });
    };

    const formatDate = (date) => {
      if (!date) return "-";
      const d = date?.toDate ? date.toDate() : new Date(date);
      return `${d.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })} ${d.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    };

    const formatTimestamp = (seconds) => {
      if (seconds == null) return null;
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}:${String(s).padStart(2, "0")}`;
    };

    const getCallComments = (callId) => comments.filter((c) => c.call_id === callId);

    const toggleExpand = (callId) => {
      setExpandedCallId((prev) => (prev === callId ? null : callId));
      setNewCommentText("");
      setEditingCommentId(null);
      setAudioTime(0);
    };

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <CircularProgress
            size="lg"
            color="primary"
          />
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {calls.length === 0 ? (
          <div className="text-center text-gray-400 py-16 bg-white rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)]">
            <p className="text-sm">{t("Aucun appel attaché à ce debrief")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {calls.map((call) => {
              const grade = call?.analysis?.call_overall_grade;
              const gradeNormalized =
                grade >= 0 ? Math.round((grade + Number.EPSILON) * 10) / 10 : -1;
              const gradeBgColor = getGradeBackgroundColor(gradeNormalized);
              const callId = call.id || "";
              const shortId = callId ? callId.substring(0, 7) : "";
              const isExpanded = expandedCallId === callId;
              const callComments = getCallComments(callId);

              return (
                <div
                  key={call.id}
                  className="bg-white rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] transition-colors hover:shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.12),0px_2px_6px_0px_rgba(0,0,0,0.10)]"
                >
                  {/* Top row: ID, date, grade, expand, menu */}
                  <div
                    className="flex items-center gap-4 px-6 py-2 cursor-pointer"
                    onClick={() => toggleExpand(callId)}
                    onKeyDown={() => {}}
                  >
                    {/* Call ID - copy to clipboard */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={() => {}}
                    >
                      <CustomCopyToClipboard
                        displayText={`#${shortId}`}
                        copyText={callId}
                        displayClassName="text-sm font-mono text-gray-900"
                        wrapperClassName="!p-1 !rounded-md"
                      />
                    </div>

                    {/* Date + time stacked */}
                    <div className="flex flex-col shrink-0">
                      <span className="text-sm text-gray-400">
                        {formatDate(call.call_timestamp).split(" ")[0]}
                      </span>
                      <span className="text-sm text-gray-400">
                        {formatDate(call.call_timestamp).split(" ")[1]}
                      </span>
                    </div>

                    {/* Grade */}
                    <div
                      className={`flex items-center font-semibold px-1.5 py-0.5 rounded text-xs ${
                        gradeNormalized >= 0 ? gradeBgColor : "bg-gray-100 text-gray-600"
                      } shrink-0`}
                    >
                      <MdOutlineStarBorder size={12} />
                      <span className="ml-0.5">
                        {gradeNormalized >= 0 ? gradeNormalized : "N.A"}
                      </span>
                    </div>

                    {/* Comment count badge */}
                    {callComments.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-primary shrink-0">
                        <FiMessageSquare size={12} />
                        <span>{callComments.length}</span>
                      </div>
                    )}

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Expand chevron */}
                    <div className="shrink-0 text-gray-400">
                      {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </div>

                    {/* Three-dot menu */}
                    <div
                      className="relative shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={() => {}}
                    >
                      <button
                        type="button"
                        className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                        onClick={() => setMenuOpenId(menuOpenId === call.id ? null : call.id)}
                      >
                        <FiMoreHorizontal size={16} />
                      </button>
                      {menuOpenId === call.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setMenuOpenId(null)}
                            onKeyDown={() => setMenuOpenId(null)}
                          />
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 z-20 min-w-[140px]">
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              onClick={() => handleRemoveCall(call.id)}
                            >
                              {t("Retirer")}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Inline audio player - always visible */}
                  {call.audio_url && (
                    <div className="px-6 pb-2">
                      <InlineAudioPlayer
                        ref={(el) => {
                          audioRefsMap.current[callId] = el;
                        }}
                        src={call.audio_url}
                        onTimeUpdate={(time) => {
                          if (expandedCallId === callId) setAudioTime(time);
                        }}
                        onPause={(time) => {
                          if (expandedCallId === callId) setAudioTime(time);
                        }}
                      />
                    </div>
                  )}

                  {/* Expanded section */}
                  {isExpanded && (
                    <div className="px-6 pb-4 border-t border-gray-100">
                      {/* Call summary */}
                      {(call?.analysis?.overview || call?.analysis?.call_summary) && (
                        <div className="mt-3 mb-2">
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                            {t("Résumé")}
                          </p>
                          <p className="text-sm text-gray-500 leading-relaxed">
                            {call.analysis.overview || call.analysis.call_summary}
                          </p>
                        </div>
                      )}

                      {/* Call chapters */}
                      {call?.analysis?.chapters && call.analysis.chapters.length > 0 && (
                        <div className="mt-2 mb-2">
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                            {t("Chapitres")}
                          </p>
                          {call.analysis.chapters.map((chapter, chapterIdx) => (
                            <div
                              key={`chapter-${chapterIdx}`}
                              className="mb-2"
                            >
                              {chapter?.title && (
                                <p className="text-sm font-medium text-gray-600 mb-0.5">
                                  {chapter.title}
                                </p>
                              )}
                              {chapter?.points?.map((point, pointIdx) => (
                                <div
                                  key={`point-${chapterIdx}-${pointIdx}`}
                                  className="flex items-start text-sm text-gray-500 ml-2 mb-0.5"
                                >
                                  <span className="mr-1.5 mt-0.5 shrink-0">&#8226;</span>
                                  <span>{point}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Comments for this call */}
                      {callComments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-gray-400 uppercase tracking-wide">
                            {t("Commentaires")}
                          </p>
                          {callComments.map((comment) => (
                            <div
                              key={comment.id}
                              className="bg-white rounded-lg p-3"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-900">
                                    {comment.author_name}
                                  </span>
                                  {comment.audio_timestamp != null && (
                                    <button
                                      type="button"
                                      className="text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded hover:bg-primary/20 transition-colors cursor-pointer"
                                      onClick={() => seekAudio(callId, comment.audio_timestamp)}
                                    >
                                      {formatTimestamp(comment.audio_timestamp)}
                                    </button>
                                  )}
                                </div>
                                {editingCommentId !== comment.id && (
                                  <div className="flex gap-0.5">
                                    <button
                                      type="button"
                                      className="p-1 rounded text-gray-400 hover:text-gray-600"
                                      onClick={() => {
                                        setEditingCommentId(comment.id);
                                        setEditingCommentContent(comment.content);
                                      }}
                                    >
                                      <MdEdit size={13} />
                                    </button>
                                    <button
                                      type="button"
                                      className="p-1 rounded text-gray-400 hover:text-red-500"
                                      onClick={() => handleDeleteComment(comment.id)}
                                    >
                                      <MdDelete size={13} />
                                    </button>
                                  </div>
                                )}
                              </div>
                              {editingCommentId === comment.id ? (
                                <div className="space-y-2 mt-1">
                                  <Textarea
                                    value={editingCommentContent}
                                    onChange={(e) => setEditingCommentContent(e.target.value)}
                                    minRows={2}
                                    size="sm"
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      color="primary"
                                      onClick={() => handleUpdateComment(comment.id)}
                                    >
                                      {t("Enregistrer")}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="light"
                                      onClick={() => {
                                        setEditingCommentId(null);
                                        setEditingCommentContent("");
                                      }}
                                    >
                                      {t("Annuler")}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {comment.content}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add comment form */}
                      <div className="mt-3">
                        <Textarea
                          placeholder={t("Ajouter un commentaire sur cet appel...")}
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                          minRows={2}
                          size="sm"
                          className="mb-2"
                        />
                        <div className="flex items-center justify-end gap-2">
                          {audioTime > 0 && (
                            <span className="text-xs text-gray-400">
                              {t("à")} {formatTimestamp(audioTime)}
                            </span>
                          )}
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleAddCallComment(callId)}
                            isLoading={addingComment}
                            isDisabled={!newCommentText.trim()}
                          >
                            {t("Commenter")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add call button - full width */}
        <button
          type="button"
          className="w-full h-11 p-4 bg-indigo-600/20 rounded-lg outline outline-1 outline-offset-[-0.50px] outline-primary flex justify-center items-center gap-2 hover:bg-indigo-600/30 transition-colors cursor-pointer"
          onClick={() => setIsSearchModalOpen(true)}
        >
          <FiPlus
            size={14}
            className="text-primary"
          />
          <span className="text-sm font-medium text-primary">{t("Ajouter un appel")}</span>
        </button>

        {/* Search Modal */}
        {isSearchModalOpen && (
          <Modal
            isOpen={isSearchModalOpen}
            showFooter={false}
            size="2xl"
            handleOnClose={() => setIsSearchModalOpen(false)}
          >
            <CallSearchModal
              existingCallIds={calls.map((c) => c.id)}
              onAddCalls={handleAddCalls}
              onClose={() => setIsSearchModalOpen(false)}
            />
          </Modal>
        )}

        {/* Confirm modal */}
        {confirmAction && (
          <ConfirmModal
            message={confirmAction.message}
            destructive={confirmAction.destructive}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
      </div>
    );
  }
);

export default DebriefCallsTab;
